import type { Client, RequestContext } from "../client";
import type { DeploymentTargetResource, EnvironmentResource, EnvironmentsSummaryResource, NewEnvironmentResource, PagingCollection, ResourceCollection, VariablesScopedToDocumentResource } from "../resources";
import type { EnvironmentSettingsMetadata, ExtensionSettingsValues } from "../resources/extensionSettingsValues";
import { BasicRepository } from "./basicRepository";
export type GetEnvironmentsRequestV2 = {
    spaceId: string;
    skip: number;
    take: number;
    partialName?: string;
    type?: EnvironmentType[];
};
export type EnvironmentType = "Static" | "Parent" | "Ephemeral";
export type GetEnvironmentsResponseV2 = {
    Environments: PagingCollection<EnvironmentV2Resource>;
};
export type BaseEnvironmentV2Resource = {
    Id: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Description: string;
};
export type StaticEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Static";
    SortOrder: number;
    UseGuidedFailure: boolean;
    AllowDynamicInfrastructure: boolean;
    ExtensionSettings: ExtensionSettingsValues[];
};
export type ParentEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Parent";
    SortOrder: number;
};
export type EphemeralEnvironmentV2Resource = BaseEnvironmentV2Resource & {
    Type: "Ephemeral";
    SortOrder: 2147483647;
};
export type EnvironmentV2Resource = StaticEnvironmentV2Resource | ParentEnvironmentV2Resource | EphemeralEnvironmentV2Resource;
export function mapFromEnvironmentResourceToEnvironmentV2Resource(environment: EnvironmentResource): EnvironmentV2Resource {
    return {
        ...environment,
        Type: "Static",
        Slug: environment.Slug ?? "",
    };
}
export class EnvironmentRepository extends BasicRepository<EnvironmentResource, NewEnvironmentResource> {
    constructor(client: Client, requestContext: RequestContext) {
        super("Environments", client, requestContext);
    }
    async create(resource: NewEnvironmentResource, args?: {}): Promise<EnvironmentResource> {
        const environment = await super.create(resource, args);
        this.client.dispatchEvent({ type: "EnvironmentCreated" });
        return environment;
    }
    sort(order: string[]) {
        return this.client.put(this.requestContext, this.client.getLink("EnvironmentSortOrder"), order);
    }
    variablesScopedOnlyToThisEnvironment(environment: EnvironmentResource): Promise<VariablesScopedToDocumentResource> {
        return this.client.get<VariablesScopedToDocumentResource>(this.requestContext, environment.Links["SinglyScopedVariableDetails"]);
    }
    machines(environment: EnvironmentResource, args?: Partial<EnvironmentMachinesArgs>): Promise<ResourceCollection<DeploymentTargetResource>> {
        return this.client.get<ResourceCollection<DeploymentTargetResource>>(this.requestContext, environment.Links["Machines"], args);
    }
    summary(args?: Partial<EnvironmentSummaryArgs>) {
        return this.client.get<EnvironmentsSummaryResource>(this.requestContext, this.client.getLink("EnvironmentsSummary"), args);
    }
    getMetadata(environment: EnvironmentResource): Promise<EnvironmentSettingsMetadata[]> {
        return this.client.get(this.requestContext, environment.Links["Metadata"], {});
    }
    getV2(request: GetEnvironmentsRequestV2): Promise<GetEnvironmentsResponseV2> {
        return this.client.get<GetEnvironmentsResponseV2>(this.requestContext, "~/api/spaces/{spaceId}/environments/v2{?skip,take,partialName,type}", request);
    }
}
export type EnvironmentMachinesArgs = {
    skip: number;
    take: number;
    partialName: string | undefined;
    roles: string | null;
    isDisabled: boolean | undefined;
    healthStatuses: string | null;
    tenantIds: string | null;
    tenantTags: string | null;
    shellNames: string | null;
    deploymentTargetTypes: string | null;
};
export type EnvironmentSummaryArgs = {
    ids: string;
    partialName: string;
    machinePartialName: string;
    roles: string;
    isDisabled: boolean;
    healthStatuses: string;
    tenantIds: string;
    tenantTags: string;
    hideEmptyEnvironments: boolean;
    shellNames: string;
    deploymentTargetTypes: string;
};
